import React, { Component } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import styled from 'styled-components';
import Dot from './Dot';
import max from 'lodash/max';
import min from 'lodash/min';

const ScatterGraphContainer = styled.div`
  font-family: ${(props) => props.theme.fonts.sfcompact_text};
`;

const LegendContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  justify-content: center;
  margin-top: 25px;

  .legendItem {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 12px;
  }

  .legendItem:not(:last-child) {
    margin-right: 30px;
  }

  .legendIconFocused {
    width: 10px;
    height: 10px;
    border: 3px solid #e25d34;
    background-color: white;
    border-radius: 50%;
  }

  .legendIcon {
    width: 10px;
    height: 10px;
    background-color: #2b3d54;
    border-radius: 50%;
  }

  p {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    margin-left: 10px;
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    flex-direction: column;
    .legendItem {
      margin-right: 0px;
      margin-top: 15px;
    }
  }
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 190px;

  .tooltipTitle {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
    font-size: 12px;
    color: #abb5be;
    margin-bottom: 9px;
  }

  .tooltipContentContainer {
    display: flex;
    flex-direction: row;
    width: 100%;
    align-items: center;
  }

  .tooltipContentContainer:not(:last-child) {
    margin-bottom: 7px;
  }

  .tooltipLabel {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-size: 12px;
    color: #1c2b38;
  }

  .tooltipValue {
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
    font-size: 12px;
    color: #2b3d54;
    margin-left: auto;
  }
`;

export default class ScatterGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hovering: false
    };
  }

  renderLegend(data) {
    return (
      <LegendContainer>
        {data.map((set, index) => (
          <div key={index} className="legendItem">
            <div className={set.focused ? 'legendIconFocused' : 'legendIcon'} />
            <p>{set.name}</p>
          </div>
        ))}
      </LegendContainer>
    );
  }

  renderTooltip() {
    const { coordinates, tooltipPayload, tooltipTitle } = this.state;
    const { tooltipContent } = this.props;
    return (
      <Tooltip
        active
        content={
          <div className="rc-tooltip overlayTooltip rc-tooltip-placement-top rc-tooltip-inner">
            <div className="rc-tooltip-content">
              <div className="rc-tooltip-arrow" />
              <div className="rc-tooltip-inner" role="tooltip">
                <TooltipContent>
                  <h3 className="tooltipTitle">{tooltipTitle}</h3>
                  <div className="tooltipContentContainer">
                    <p className="tooltipLabel">{tooltipContent.xLabel}</p>
                    <p className="tooltipValue">
                      {tooltipPayload.x
                        ? Number(tooltipPayload.x).toFixed(1)
                        : 0}
                    </p>
                  </div>
                  <div className="tooltipContentContainer">
                    <p className="tooltipLabel">{tooltipContent.yLabel}</p>
                    <p className="tooltipValue">
                      {tooltipPayload.y
                        ? Number(tooltipPayload.y).toFixed(1)
                        : 0}
                    </p>
                  </div>
                </TooltipContent>
              </div>
            </div>
          </div>
        }
        cursor={false}
        position={coordinates}
      />
    );
  }

  getDomain() {
    const { data } = this.props;
    const values = data
      .map((set) => set.value.map((item) => Number(item.x)))
      .flat();
    const dataMax = max(values);
    const dataMin = min(values);
    return [
      isFinite(dataMin) && dataMin > 10
        ? parseInt(Number(dataMin) - Number(dataMin) * 0.05, 10)
        : 0,
      isFinite(dataMax) && dataMax > 0
        ? parseInt(Number(dataMax) + Number(dataMax) * 0.05, 10)
        : 0
    ];
  }

  render() {
    const { data, currency } = this.props;
    const { hovering } = this.state;
    const domain = this.getDomain();

    return (
      <ScatterGraphContainer>
        <ResponsiveContainer height={550} width="100%">
          <ScatterChart height={550}>
            <CartesianGrid stroke="#e4e4e4" />
            <XAxis
              interval="preserveEnd"
              axisLine={false}
              type="number"
              tickCount={6}
              tickFormatter={(value) =>
                currency
                  ? value.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: currency,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })
                  : value
              }
              tickMargin={8}
              tickSize={0}
              tickLine={{ stroke: '#e4e4e4' }}
              domain={domain}
              dataKey="x"
            />
            <YAxis
              domain={[90, 100]}
              scale="linear"
              type="number"
              axisLine={false}
              width={140}
              tickMargin={20}
              tickSize={80}
              tickLine={{ stroke: '#e4e4e4' }}
              dataKey="y"
            />
            <Legend content={() => this.renderLegend(data)} />
            {hovering && this.renderTooltip()}
            {data.map((set) => (
              <Scatter
                onMouseEnter={(props) =>
                  this.setState({
                    hovering: set.name,
                    tooltipPayload: props.payload,
                    tooltipTitle: props.nameSet,
                    coordinates: {
                      x: props.x - 110,
                      y: props.y - 130
                    }
                  })
                }
                onMouseOut={() => this.setState({ hovering: false })}
                key={set.name}
                name={set.name}
                data={set.value}
                shape={(props) => <Dot {...props} set={set} />}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </ScatterGraphContainer>
    );
  }
}
